import { Trans } from "@lingui/react/macro";
import { useCallback, useEffect, useState } from "react";
import { ConfirmationAction } from "@/components/ai-elements/confirmation";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import { logger } from "@/lib/logger";
import { UI_CONFIRM_YES } from "../shared/constants";
import {
  useApprovalSubmissionGuard,
  useLegacyToolApprovalPermission,
  useNativeToolApproval,
} from "./native-tool-approval";
import { useToolSuccessEffect } from "./tool-success-effects-context";
import type { ConfirmProps } from "./types";

const autoApprovalAttemptedToolCallIds = new Set<string>();

const AUTO_APPROVAL_BLOCKED_TOOL_PATTERNS = [
  /^delete_/,
  /^remove_/,
  /^invite_/,
  /(^|_)service_account$/,
  /_service_account$/,
  /^register_service_account$/,
  /^edit_user_role$/,
];

type ApprovalSource = "manual" | "auto";

function canAutoApproveTool(toolName: string): boolean {
  return !AUTO_APPROVAL_BLOCKED_TOOL_PATTERNS.some((pattern) =>
    pattern.test(toolName),
  );
}

function getToolCallKey(toolName: string, toolCallId: string): string {
  return `${toolName}:${toolCallId}`;
}

/**
 * Tool 実行の承認を native / legacy の適切な応答経路へ送る。
 *
 * chat 作成者の権限確認、同一 Tool call の二重送信防止、応答後の continuation を
 * この境界で直列化し、失敗時は再試行可能な表示へ戻す。
 */
export const ApproveButton = ({
  toolCallId,
  toolName,
  addToolResult,
  sendMessage,
  children,
  disabled = false,
  variant = "default",
  className,
  onSuccess,
  overrideInput,
}: {
  toolCallId: string;
  toolName: string;
  addToolResult: ConfirmProps["addToolResult"];
  sendMessage: ConfirmProps["sendMessage"];
  children: React.ReactNode;
  disabled?: boolean;
  variant?: React.ComponentProps<typeof ConfirmationAction>["variant"];
  className?: string;
  onSuccess?: () => void;
  /** 段階移行中の互換用。native approval では署名済み input だけを使用する。 */
  overrideInput?: unknown;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const notifyToolSuccess = useToolSuccessEffect();
  const { autoApproveToolConfirmations } = useAiChatUiContext();
  const nativeApproval = useNativeToolApproval();
  const canUseLegacyApproval = useLegacyToolApprovalPermission(
    nativeApproval === null,
  );
  const runApprovalSubmission = useApprovalSubmissionGuard();
  const canApprove = nativeApproval?.canRespond ?? canUseLegacyApproval;
  const toolCallKey = getToolCallKey(toolName, toolCallId);

  const submitApproval = useCallback(
    async (approvalSource: ApprovalSource) => {
      if (!canApprove) {
        return;
      }

      const didSubmit = await runApprovalSubmission(toolCallKey, async () => {
        setIsSubmitting(true);
        setSubmissionFailed(false);
        try {
          if (nativeApproval) {
            await nativeApproval.addToolApprovalResponse({
              id: nativeApproval.approvalId,
              approved: true,
            });
            await sendMessage();
          } else {
            await addToolResult({
              toolCallId,
              tool: toolName,
              output: {
                type: UI_CONFIRM_YES,
                confirm: true,
                approvalSource,
                ...(overrideInput === undefined
                  ? {}
                  : { input: overrideInput }),
              },
            });
            await sendMessage();
          }
        } catch (error) {
          setSubmissionFailed(true);
          logger.error(error, {
            component: "ApproveButton",
            toolCallId,
            toolName,
          });
          throw error;
        } finally {
          setIsSubmitting(false);
        }
      });

      if (didSubmit) {
        onSuccess?.();
        notifyToolSuccess({ toolName, toolCallId });
      }
    },
    [
      addToolResult,
      canApprove,
      nativeApproval,
      notifyToolSuccess,
      onSuccess,
      overrideInput,
      runApprovalSubmission,
      sendMessage,
      toolCallId,
      toolCallKey,
      toolName,
    ],
  );

  useEffect(() => {
    if (
      nativeApproval !== null ||
      !autoApproveToolConfirmations ||
      !canAutoApproveTool(toolName) ||
      !canApprove ||
      isSubmitting ||
      autoApprovalAttemptedToolCallIds.has(toolCallKey)
    ) {
      return;
    }

    autoApprovalAttemptedToolCallIds.add(toolCallKey);
    void submitApproval("auto").catch(() => undefined);
  }, [
    autoApproveToolConfirmations,
    canApprove,
    isSubmitting,
    nativeApproval,
    submitApproval,
    toolCallKey,
    toolName,
  ]);

  return (
    <div className="space-y-2">
      <ConfirmationAction
        aria-describedby={nativeApproval?.responseDisabledReasonId}
        disabled={disabled || !canApprove || isSubmitting}
        variant={variant}
        className={className}
        onClick={() => void submitApproval("manual").catch(() => undefined)}
      >
        {children}
      </ConfirmationAction>
      {submissionFailed ? (
        <p className="text-destructive text-xs" role="alert">
          <Trans>Could not submit the approval. Please try again.</Trans>
        </p>
      ) : null}
    </div>
  );
};
